import { addDays } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';

import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';

import { LprHotListAPI, SnLpr, SnLprObject } from '../../api';

@Component({
  selector: 'fs-add-lprhotlist-form',
  templateUrl: './add-lprhotlist-form.component.html',
  styleUrls: ['./add-lprhotlist-form.component.scss'],
})
export class AddLprhotlistFormComponent implements AfterViewInit, OnInit, OnDestroy {
  public form = new UntypedFormGroup({});
  public dataSource = new MatTableDataSource();

  public formFields: SnFormFieldConfig[] = [
    {
      key: 'plate',
      type: 'input',
      className: 'mb-2',
      templateOptions: {
        label: t('vehicleIntelligence.hotlist.plate'),
        required: true,
        appearance: 'fill',
      },
      validators: {
        duplicates: {
          expression: (c: AbstractControl) => {
            return this.data?.hotlist?.properties?.disableDuplicates
              ? this.data.lprObjects.findIndex(
                  (obj) => obj.plate.toLowerCase() === c.value?.toString()?.toLowerCase(),
                ) <= -1
              : true;
          },
          message: (error: any, field: SnFormFieldConfig) =>
            t('vehicleIntelligence.hotlist.validation.alreadyExistsInHotlist', {
              plate: field.formControl.value,
            }),
        },
      },
    },
    {
      key: 'properties.hotlistData.plateState',
      type: 'select',
      templateOptions: {
        label: t('vehicleIntelligence.hotlist.plateState'),
        options: [
          { label: 'AL', value: 'AL' },
          { label: 'AK', value: 'AK' },
          { label: 'AS', value: 'AS' },
          { label: 'AZ', value: 'AZ' },
          { label: 'AR', value: 'AR' },
          { label: 'CA', value: 'CA' },
          { label: 'CO', value: 'CO' },
          { label: 'CT', value: 'CT' },
          { label: 'DE', value: 'DE' },
          { label: 'DC', value: 'DC' },
          { label: 'FM', value: 'FM' },
          { label: 'FL', value: 'FL' },
          { label: 'GA', value: 'GA' },
          { label: 'GU', value: 'GU' },
          { label: 'HI', value: 'HI' },
          { label: 'ID', value: 'ID' },
          { label: 'IL', value: 'IL' },
          { label: 'IN', value: 'IN' },
          { label: 'IA', value: 'IA' },
          { label: 'KS', value: 'KS' },
          { label: 'KY', value: 'KY' },
          { label: 'LA', value: 'LA' },
          { label: 'ME', value: 'ME' },
          { label: 'MH', value: 'MH' },
          { label: 'MD', value: 'MD' },
          { label: 'MA', value: 'MA' },
          { label: 'MI', value: 'MI' },
          { label: 'MN', value: 'MN' },
          { label: 'MS', value: 'MS' },
          { label: 'MO', value: 'MO' },
          { label: 'MT', value: 'MT' },
          { label: 'NE', value: 'NE' },
          { label: 'NV', value: 'NV' },
          { label: 'NH', value: 'NH' },
          { label: 'NJ', value: 'NJ' },
          { label: 'NM', value: 'NM' },
          { label: 'NY', value: 'NY' },
          { label: 'NC', value: 'NC' },
          { label: 'ND', value: 'ND' },
          { label: 'MP', value: 'MP' },
          { label: 'OH', value: 'OH' },
          { label: 'OK', value: 'OK' },
          { label: 'OR', value: 'OR' },
          { label: 'PW', value: 'PW' },
          { label: 'PA', value: 'PA' },
          { label: 'PR', value: 'PR' },
          { label: 'RI', value: 'RI' },
          { label: 'SC', value: 'SC' },
          { label: 'SD', value: 'SD' },
          { label: 'TN', value: 'TN' },
          { label: 'TX', value: 'TX' },
          { label: 'UT', value: 'UT' },
          { label: 'VT', value: 'VT' },
          { label: 'VI', value: 'VI' },
          { label: 'VA', value: 'VA' },
          { label: 'WA', value: 'WA' },
          { label: 'WV', value: 'WV' },
          { label: 'WI', value: 'WI' },
          { label: 'WY', value: 'WY' },
        ],
        required: false,
        appearance: 'fill',
      },
    },
    {
      key: 'expiresTimestamp',
      type: 'datetimepicker',
      className: 'fs-col-6',
      templateOptions: {
        label: t('common.endDate'),
        appearance: 'fill',
        minDate: () => new Date(),
      },
    },
    {
      key: 'properties.reason',
      type: 'input',
      templateOptions: {
        label: t('settings.demoPanel.alprAlert.reason'),
        required: true,
        appearance: 'fill',
      },
      hideExpression: () => this.data.hotlist.properties.reasonOptions?.length > 0,
    },
    {
      key: 'properties.reason',
      type: 'select',
      templateOptions: {
        label: t('settings.demoPanel.alprAlert.reason'),
        required: true,
        options: this.data.hotlist.properties.reasonOptions?.map((option) => {
          return {
            label: option,
            value: option,
          };
        }),
      },
      hideExpression: () => this.data.hotlist.properties.reasonOptions?.length < 1,
    },
  ];

  public formModel: any = {};

  private _destroy$ = new Subject();

  constructor(
    private readonly _dialogRef: MatDialogRef<AddLprhotlistFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      hotlist: SnLpr;
      plate: SnLprObject;
      mode: string;
      lprObjects: SnLprObject[];
    },
    private readonly _lprService: LprHotListAPI,
  ) {
    this.formModel.lprHotlist = data.hotlist.id;
  }

  public cancel(): void {
    this.form.reset();
    this._dialogRef.close();
  }

  public submitForm(): void {
    if (this.data.mode === 'add') {
      this._lprService
        .addPlateToLprObject({
          ...this.formModel,
          reason: this.formModel.properties.reason,
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe((plate) => {
          this._dialogRef.close(plate);
        });
    }
    if (this.data.mode === 'edit') {
      this._lprService
        .updateLprObject(this.data.plate.id, {
          properties: this.formModel['properties'],
          plate: this.formModel['plate'],
          expiresTimestamp: this.formModel['expiresTimestamp'],
          lprHotlist: this.formModel['hotlist'],
          reason: this.formModel.properties['reason'],
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe((plate) => {
          this._dialogRef.close(plate);
        });
    }
  }

  public ngOnInit(): void {
    this.data.hotlist?.properties.fields.forEach((value: any) => {
      const fieldOptions = value.options?.map(({ option }) => {
        return {
          label: option,
          value: option,
        };
      });

      this.formFields.push(
        {
          key: `properties.hotlistData.${value['fieldName']}`,
          type: 'textarea',
          templateOptions: {
            label: value['fieldName'],
            required: value['required'] === true ? true : false,
            appearance: 'fill',
            rows: 5,
            minLength: value['minLength'] !== null ? value['minLength'] : null,
            maxLength: value['maxLength'] !== null ? value['maxLength'] : null,
          },
          validation: {
            messages: {
              minlength: t('vehicleIntelligence.hotlist.validation.minLengthGeneric'),
            },
          },
          hideExpression: () => value['fieldType'] !== 'textArea',
        },

        {
          key: `properties.hotlistData.${value['fieldName']}`,
          type: 'input',
          templateOptions: {
            label: value['fieldName'],
            required:
              value['required'] === true || value['fieldName'] === 'Case Number' ? true : false,
            appearance: 'fill',
            minLength:
              value['fieldName'] === 'Case Number'
                ? 9
                : value['minLength'] !== null
                ? value['minLength']
                : null,
            maxLength:
              value['fieldName'] === 'Case Number'
                ? 9
                : value['maxLength'] !== null
                ? value['maxLength']
                : null,
          },
          validators: {
            validation:
              value['validation'] === 'alphanumeric'
                ? ['alphanumeric']
                : value['validation'] === 'alpha'
                ? ['alpha']
                : value['fieldName'] === 'Case Number'
                ? ['numeric']
                : [],
          },
          validation: {
            messages: {
              minlength:
                value['fieldName'] === 'Case Number'
                  ? t('vehicleIntelligence.hotlist.validation.caseNumberMinLength')
                  : t('vehicleIntelligence.hotlist.validation.minLengthGeneric'),
            },
          },
          hideExpression: () => value['fieldType'] !== 'smallText',
        },

        {
          key: `properties.hotlistData.${value['fieldName']}`,
          type: 'select',
          templateOptions: {
            label: value['fieldName'],
            required: value['required'] === true ? true : false,
            appearance: 'fill',
            options: fieldOptions,
          },

          hideExpression: () => value['fieldType'] !== 'select',
        },
      );
    });
    if (this.data.mode === 'add') {
      const expDays = +this.data.hotlist.defaultExpirationDuration.replace(/\D/g, '');
      this.formModel.expiresTimestamp = addDays(new Date(), expDays);
    }
  }

  public ngAfterViewInit(): void {
    if (this.data.mode === 'edit') {
      this.formModel = {
        ...this.data.plate,
        plate: this.data.plate.plate,
        hotlist: this.data.hotlist.id,
      };
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
